import { DEFAULT_VOICE } from './ttsClient';

// ── the reader's voice and speed ─────────────────────────────────────────────
// Which voices the reader offers, the speed range each reads at, and the
// visitor's last voice plus the speed they last chose for each voice —
// remembered in localStorage the way useThemeMode remembers light/dark.
//
// Only the visitor's own picks are stored. A voice whose speed they never
// moved keeps following its `initial` below, so retuning a default still
// reaches them.

// The voice selector's options, in display order. Sekuru is off for now.
export const READER_VOICES = ['Woman', DEFAULT_VOICE, 'TonyNormal'];

// Per-voice reading speeds. Voices default to 0.7× for a natural reading pace
// and a lone word gets 0.6× so it isn't clipped (see the reader textarea's
// onBlur). Tony reads at 0.5× for both and ranges from 0.25× to 0.85×; every
// other voice ranges from 0.5× to 1.5×.
const STANDARD_SPEEDS = { initial: 0.7, singleWord: 0.6, min: 0.5, max: 1.5 };
const VOICE_SPEEDS = {
  TonyNormal: { initial: 0.5, singleWord: 0.5, min: 0.25, max: 0.85 },
};
export const speedsFor = voice => VOICE_SPEEDS[voice] ?? STANDARD_SPEEDS;

// Stored as { voice, speeds: { [voice]: speed } }.
const STORAGE_KEY = 'shonaReaderVoice';

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const isOffered = voice => READER_VOICES.includes(voice);

// What is stored, minus anything unusable: a voice the selector no longer
// offers, or a speed that isn't a number. Dropping them here also keeps them
// out of the next write.
const readStored = () => {
  const empty = { voice: null, speeds: {} };
  // The build-time pre-render has no window; it draws the defaults.
  if (typeof window === 'undefined') return empty;
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return {
      voice: isOffered(stored?.voice) ? stored.voice : null,
      speeds: Object.fromEntries(
        Object.entries(stored?.speeds ?? {}).filter(
          ([voice, speed]) => isOffered(voice) && Number.isFinite(speed)
        )
      ),
    };
  } catch {
    // Unparseable JSON, or Safari in private mode throwing on access.
    return empty;
  }
};

const write = prefs => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // The choice still applies on screen; it just won't persist.
  }
};

export const initialVoice = () => readStored().voice ?? DEFAULT_VOICE;

// The speed last chosen for `voice`, pulled back into the voice's current
// range so retuning it can't leave the slider outside its track; the voice's
// initial speed if none was chosen.
export const rememberedSpeed = voice => {
  const { initial, min, max } = speedsFor(voice);
  const stored = readStored().speeds[voice];
  return stored === undefined ? initial : clamp(stored, min, max);
};

export const rememberVoice = voice => write({ ...readStored(), voice });

export const rememberSpeed = (voice, speed) => {
  const stored = readStored();
  write({ ...stored, speeds: { ...stored.speeds, [voice]: speed } });
};
